
import { useEffect, useState, useRef } from 'react';
import { fetchStaffVoices, type StaffVoiceItem } from '../../../lib/wordpress';
import StaffVoiceCard from './StaffVoiceCard';

const FEATURED_STAFF_IDS = [380, 394, 396];

export default function StaffVoiceSection() {
  const [staffList, setStaffList] = useState<StaffVoiceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // PC用インデックス
  const [pcIndex, setPcIndex] = useState(0);
  const PC_COLS = 3;

  useEffect(() => {
    const loadStaffVoices = async () => {
      try {
        setIsLoading(true);
        const data = await fetchStaffVoices(FEATURED_STAFF_IDS);
        setStaffList(data);
      } catch (err) {
        console.error('スタッフの声の取得に失敗:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadStaffVoices();
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const cardWidth = container.offsetWidth * 0.82;
      const index = Math.round(container.scrollLeft / cardWidth);
      setActiveIndex(Math.max(0, Math.min(index, staffList.length - 1)));
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [staffList.length]);

  const handleDotClick = (index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const cardWidth = container.offsetWidth * 0.82;
    container.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
  };

  // SP矢印
  const handleSpArrow = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const cardWidth = container.offsetWidth * 0.82;
    const newIndex =
      direction === 'left'
        ? Math.max(0, activeIndex - 1)
        : Math.min(staffList.length - 1, activeIndex + 1);
    container.scrollTo({ left: newIndex * cardWidth, behavior: 'smooth' });
  };

  // PC矢印（3件固定なので左右は不要だが、将来的な拡張のため実装）
  const pcCanLeft = pcIndex > 0;
  const pcCanRight = pcIndex + PC_COLS < staffList.length;

  const handlePcArrow = (direction: 'left' | 'right') => {
    setPcIndex((prev) =>
      direction === 'left'
        ? Math.max(0, prev - 1)
        : Math.min(staffList.length - PC_COLS, prev + 1)
    );
  };

  const visibleStaff = staffList.slice(pcIndex, pcIndex + PC_COLS);

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* セクションヘッダー */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            派遣スタッフの声
          </h2>
          <p className="text-lg text-gray-600">勤務スタイルや収入例、リアルな感想をぜひご覧ください。</p>
        </div>

        {/* ローディング */}
        {isLoading && (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* PC表示：3カラムグリッド + 矢印 */}
        {!isLoading && staffList.length > 0 && (
          <div className="hidden md:block mb-12">
            <div className="relative">
              {/* 左矢印 */}
              {pcCanLeft && (
                <button
                  onClick={() => handlePcArrow('left')}
                  className="absolute -left-6 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full shadow-xl shadow-gray-400/50 transition-all w-12 h-12 flex items-center justify-center cursor-pointer"
                  aria-label="前へ"
                >
                  <i className="ri-arrow-left-s-line text-3xl text-blue-500"></i>
                </button>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">
                {visibleStaff.map((staff) => (
                  <StaffVoiceCard key={staff.id} staff={staff} />
                ))}
              </div>

              {/* 右矢印 */}
              {pcCanRight && (
                <button
                  onClick={() => handlePcArrow('right')}
                  className="absolute -right-6 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full shadow-xl shadow-gray-400/50 transition-all w-12 h-12 flex items-center justify-center cursor-pointer"
                  aria-label="次へ"
                >
                  <i className="ri-arrow-right-s-line text-3xl text-blue-500"></i>
                </button>
              )}
            </div>
          </div>
        )}

        {/* SP表示：カルーセル + 矢印 */}
        {!isLoading && staffList.length > 0 && (
          <div className="md:hidden mb-6">
            <div className="relative">
              {/* 左矢印 */}
              {activeIndex > 0 && (
                <button
                  onClick={() => handleSpArrow('left')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full shadow-xl shadow-gray-400/50 transition-all w-10 h-10 flex items-center justify-center cursor-pointer"
                  style={{ marginLeft: '-4px' }}
                  aria-label="前へ"
                >
                  <i className="ri-arrow-left-s-line text-2xl text-blue-500"></i>
                </button>
              )}

              <div
                ref={scrollContainerRef}
                className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide"
                style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
              >
                {/* 左パディング */}
                <div className="flex-shrink-0 w-4" />
                {staffList.map((staff) => (
                  <div
                    key={staff.id}
                    className="flex-shrink-0 snap-center"
                    style={{ width: '82%' }}
                  >
                    <StaffVoiceCard staff={staff} />
                  </div>
                ))}
                {/* 右パディング（チラ見せ用） */}
                <div className="flex-shrink-0 w-4" />
              </div>

              {/* 右矢印 */}
              {activeIndex < staffList.length - 1 && (
                <button
                  onClick={() => handleSpArrow('right')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full shadow-xl shadow-gray-400/50 transition-all w-10 h-10 flex items-center justify-center cursor-pointer"
                  style={{ marginRight: '-4px' }}
                  aria-label="次へ"
                >
                  <i className="ri-arrow-right-s-line text-2xl text-blue-500"></i>
                </button>
              )}
            </div>

            {/* ドットインジケーター */}
            {staffList.length > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                {staffList.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`transition-all duration-300 cursor-pointer rounded-full ${
                      activeIndex === index
                        ? 'w-8 h-4 bg-blue-500'
                        : 'w-4 h-4 bg-gray-300 hover:bg-blue-300'
                    }`}
                    aria-label={`スタッフ ${index + 1} に移動`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* データなし */}
        {!isLoading && staffList.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-user-line text-4xl"></i>
            </div>
            <p className="text-base">現在表示できるスタッフの声がありません</p>
          </div>
        )}

        {/* もっと見るボタン */}
        {!isLoading && staffList.length > 0 && (
          <div className="text-center">
            <a
              href="/staff-voice"
              onClick={(e) => {
                e.preventDefault();
                window.REACT_APP_NAVIGATE('/staff-voice');
              }}
              className="inline-flex items-center px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              <span>もっと見る</span>
              <div className="w-5 h-5 flex items-center justify-center ml-2">
                <i className="ri-arrow-right-line text-lg"></i>
              </div>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
